// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Cross-flight trend analysis runtime + view orchestration:
// - trendAircraftOptions / setupTrendControls / populateTrendAircraftSelect /
//   selectedTrendAircraft / selectedTrendAircraftDatabaseId: aircraft
//   selector + metric tile + table mode/sort wiring.
// - trendCacheKey / clearTrendRowsForAircraft: cache key derivation
//   from manifest + profile + sensitivity + review settings, memoized.
// - buildAircraftTrends: client-side trend row construction by parsing
//   each flight and computing its health summary.
// - maybeLoadDatabaseTrendRows / scheduleTrendSnapshotRefresh: server
//   snapshot fetch + background staleness refresh.
// - renderCurrentTrendView / renderCachedTrendView / renderTrendView /
//   setTrendAnalysisBusy: render entry points + busy/empty/canceled
//   states for the trend view.
// - renderTrendCanceledView / renderTrendUnavailableView: empty state
//   composition for the trend view header + story + table + samples.
// - cancelTrendAnalysis / renderTrendsForSelection: the user-facing
//   "analyze now" + "cancel" entry points.
// - trendEvidenceReturnContext / updateTrendReturnControls /
//   trendEvidenceTopReviewItem / focusTrendEvidenceFlightReview /
//   markReturnedTrendEvidenceRow / returnToTrendEvidence: the
//   round-trip between the trends tab and an individual flight when
//   the user opens a flight from a trend evidence row.
// - openTrendFlight: thin wrapper around loadFlightLog that preserves
//   the trend-return context.

export function createTrendController({
  escapeHtml,
  trendMetricMeta,
  trendCacheSchemaVersion,
  trendFlightFingerprint,
  trendHashString,
  visibleFlightEntries,
  trendExclusionCounts = () => ({}),
  isSupportedFlight,
  selectedAircraftWorkspace,
  reviewSettingsSignature,
  flightIncludedInTrendAnalysis,
  parseFlightEntry,
  trimEndpointSamples,
  calculateHealthSummary,
  // Off-main-thread + persistently-cached variant of calculateHealthSummary used
  // by the per-flight build loop below. Optional: falls back to the synchronous
  // calculateHealthSummary when not injected (e.g. in unit harnesses).
  analyzeTrendFlight,
  aircraftProfileForEntry,
  buildTrendRow,
  flightDisplayTitle,
  trendDateValue,
  trendDateLabel,
  normalizeTrendRowsForDisplay,
  readDatabaseTrendSnapshotRows,
  saveDatabaseTrendSnapshotRows,
  // Phase 3i: persistent (IndexedDB) trend-snapshot cache (optional; absent in unit harnesses).
  readCachedTrendSnapshot = null,
  writeCachedTrendSnapshot = null,
  deleteCachedTrendSnapshotsForAircraft = null,
  readStoredTrendRows,
  writeStoredTrendRows,
  removeStoredTrendRows,
  clearStoredTrendRowsForAircraft,
  waitForUiFrame,
  engineReviewItems,
  selectedDetectionWindows,
  selectCompanionSeriesForHealthCard,
  renderHealthSummary,
  setPlaybackScrubElapsed,
  updateHealthCardSelection,
  scheduleDrawAll,
  setAircraftPanel,
  reanalyzeAllFlights = null,
  renderAircraftWorkspace,
  trendRowsIncludedByPreference,
  trendRenderSignature,
  trendDataRenderSignature,
  updateTrendMetricControls,
  renderTrendOwnerStory,
  renderTrendVisualWorkspace,
  renderTrendSummary,
  renderTrendTable,
  renderTrendSampleGraphs,
  scheduleTrendSampleGraphPrewarm,
  syncTrendRoute = () => {},
  setTrendEvidenceMode,
  setTrendEvidenceSort,
  setTrendEvidenceLayout,
  loadFlightLog,
  activateView,
  selectReportPanel,
  scrollFlightChartPanelIntoView,
  trendCache,
  trendExcludedFlightIds,
  trendIncludedFlightIds,
  getFlightManifest,
  getAircraftProfiles,
  getSelectedFlight,
  getSelectedAircraftPanel,
  getActiveTrendMetric,
  setActiveTrendMetric,
  getDetectionSensitivity,
  getHealthSummary,
  getCurrentTrendRows,
  setCurrentTrendRows,
  getCurrentTrendAircraft,
  setCurrentTrendAircraft,
  getCurrentTrendSensitivity = () => "",
  setCurrentTrendSensitivity = () => {},
  setSelectedCylinderNumber,
  setSelectedEngineScope,
  getSelectedHealthKey,
  setSelectedHealthKey,
  getTrendRequestToken,
  incrementTrendRequestToken,
  getTrendReturnContext,
  setTrendReturnContext,
  getRenderedTrendSignature,
  setRenderedTrendSignature,
  getRenderedTrendDataSignature,
  setRenderedTrendDataSignature,
  setRenderedTrendFlushed,
  getRenderedTrendFlushed,
  setRenderedTrendSampleSignature,
  incrementTrendSampleGraphToken,
  setRenderedTrendTableSignature,
}) {
  const trendCacheKeyMemo = new Map();
  let staleTrendSnapshotState = null;
  const TREND_STALE_REFRESH_DELAY_MS = 4000;
  const TREND_STALE_REFRESH_MAX_ATTEMPTS = 15;
  // Last content ETag the server issued for each cache key, used to make the background
  // snapshot revalidation a conditional GET (If-None-Match -> 304). Kept in lockstep with
  // trendCache: cleared wherever the cached rows are cleared so a 304 always corresponds to
  // rows we still hold. Only set from a FRESH (non-stale) response.
  const trendEtagStore = new Map();

  function trendAircraftOptions() {
    return [...new Set(visibleFlightEntries()
      .filter(isSupportedFlight)
      .map((entry) => entry.aircraft))]
      .filter(Boolean)
      .sort();
  }

  function setupTrendControls() {
    document.addEventListener("change", (event) => {
      if (event.target?.id !== "trendHistoryStart") return;
      try { localStorage.setItem(`alaryiq.trendHistoryStart:${selectedTrendAircraft()}`, event.target.value || ""); } catch {}
      renderCurrentTrendView({ force: true });
    });
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-trend-metric]");
      if (!button || !button.closest("#trendMetricTiles")) return;
      const key = button.dataset.trendMetric || "overallScore";
      if (!trendMetricMeta[key]) return;
      setActiveTrendMetric(key);
      renderCurrentTrendView({ force: true });
    });
    document.addEventListener("click", async (event) => {
      const modeButton = event.target.closest("[data-trend-table-mode]");
      if (modeButton) {
        setTrendEvidenceMode(modeButton.dataset.trendTableMode);
        return;
      }
      const sortButton = event.target.closest("[data-trend-table-sort]");
      if (sortButton) {
        setTrendEvidenceSort(sortButton.dataset.trendTableSort);
        return;
      }
      const layoutButton = event.target.closest("[data-trend-table-layout]");
      if (layoutButton) {
        setTrendEvidenceLayout(layoutButton.dataset.trendTableLayout);
        return;
      }
      const emptyActionButton = event.target.closest("[data-trend-empty-action]");
      if (emptyActionButton && emptyActionButton.closest("#view-trends")) {
        const action = emptyActionButton.dataset.trendEmptyAction;
        if (action === "retry") renderTrendsForSelection({ force: true });
        else if (action === "upload") setAircraftPanel("uploads");
        else if (action === "reanalyze") await reanalyzeTrendFlightsFromEmptyState(emptyActionButton);
      }
    });
    document.addEventListener("change", (event) => {
      if (event.target?.id !== "trendAircraftSelect") return;
      if (document.querySelector("#view-aircraft")?.classList.contains("is-active") && getSelectedAircraftPanel() === "trends") {
        document.querySelector(".subhead").textContent = `${selectedTrendAircraft() || "Aircraft"} · cross-flight engine history`;
      }
      syncTrendRoute(selectedTrendAircraft());
      renderTrendsForSelection();
    });
  }

  async function reanalyzeTrendFlightsFromEmptyState(button = null) {
    const aircraft = selectedTrendAircraft();
    if (!aircraft || typeof reanalyzeAllFlights !== "function") {
      setAircraftPanel("aircraft");
      return;
    }
    const entries = visibleFlightEntries().filter((entry) => entry.aircraft === aircraft);
    if (button) button.disabled = true;
    try {
      const result = await reanalyzeAllFlights(aircraft, entries);
      if (result?.started) {
        clearTrendRowsForAircraft(aircraft);
        await renderTrendsForSelection({ force: true });
      } else if (result?.reason === "no-flights") {
        setAircraftPanel("aircraft");
      }
    } finally {
      if (button) button.disabled = false;
    }
  }

  function syncTrendStaleNotice(aircraft, { stale = false, reason = "" } = {}) {
    const notice = document.querySelector("#trendStaleNotice");
    if (!notice) return;
    // Keep saved rows visible and identify when a refresh is still pending.
    staleTrendSnapshotState = stale && aircraft ? { aircraft, reason } : null;
    notice.hidden = !stale;
    notice.innerHTML = stale ? `<span>Showing saved analysis. ${reason === "stale_analysis_runs" ? "Update these flights for current results." : reason === "refresh_delayed" ? "Refresh is delayed." : "Newer results are still being prepared."}</span>` : "";
  }

  function renderCurrentTrendView({ force = false } = {}) {
    const aircraft = selectedTrendAircraft();
    const sensitivity = getDetectionSensitivity();
    const currentTrendRows = getCurrentTrendRows();
    const currentTrendAircraft = getCurrentTrendAircraft();
    const currentSensitivity = getCurrentTrendSensitivity();
    if (
      currentTrendRows
      && (!currentTrendAircraft || currentTrendAircraft === aircraft)
      && (!currentSensitivity || currentSensitivity === sensitivity)
    ) {
      renderTrendView(currentTrendRows, currentTrendAircraft || aircraft, { force });
      return true;
    }
    renderCachedTrendView({ force });
    return false;
  }

  function cachedTrendRowsUsable(rows) {
    return trendRowsIncludedByPreference(rows ?? [])
      .some((row) => !row?.error && Number.isFinite(row?.overallScore));
  }

  function cachedTrendRowsForDisplay(key) {
    const rows = trendCache.get(key) ?? readStoredTrendRows(key);
    if (!rows) return null;
    if (cachedTrendRowsUsable(rows)) {
      if (!trendCache.has(key)) trendCache.set(key, rows);
      return rows;
    }
    trendCache.delete(key);
    trendEtagStore.delete(key);
    removeStoredTrendRows(key);
    return null;
  }

  function populateTrendAircraftSelect(preferredAircraft = getSelectedFlight()?.aircraft) {
    const select = document.querySelector("#trendAircraftSelect");
    if (!select) return;
    const aircraft = trendAircraftOptions();
    const current = preferredAircraft && aircraft.includes(preferredAircraft)
      ? preferredAircraft
      : select.value && aircraft.includes(select.value)
        ? select.value
        : aircraft[0] ?? "";
    select.innerHTML = aircraft.map((tail) => `<option value="${escapeHtml(tail)}">${escapeHtml(tail)}</option>`).join("");
    select.disabled = !aircraft.length;
    select.value = current;
  }

  function selectedTrendAircraft() {
    const selectedValue = document.querySelector("#trendAircraftSelect")?.value;
    if (selectedValue) return selectedValue;
    return selectedAircraftWorkspace() || getSelectedFlight()?.aircraft || trendAircraftOptions()[0] || "";
  }

  function selectedTrendAircraftDatabaseId(aircraft = selectedTrendAircraft()) {
    const selectedFlight = getSelectedFlight();
    const selectedAircraftId = selectedFlight?.aircraft === aircraft ? selectedFlight?.database?.aircraftId : "";
    if (selectedAircraftId) return selectedAircraftId;
    const match = visibleFlightEntries().find((entry) => entry.aircraft === aircraft && entry.database?.aircraftId);
    return match?.database?.aircraftId ?? "";
  }

  function trendCacheKey(aircraft) {
    const flightManifest = getFlightManifest();
    const aircraftProfiles = getAircraftProfiles();
    const detectionSensitivity = getDetectionSensitivity();
    const reviewSignature = reviewSettingsSignature(aircraft);
    const cached = trendCacheKeyMemo.get(aircraft);
    if (
      cached
      && cached.manifestRef === flightManifest
      && cached.profilesRef === aircraftProfiles
      && cached.excludedRef === trendExcludedFlightIds
      && cached.includedRef === trendIncludedFlightIds
      && cached.excludedSize === trendExcludedFlightIds.size
      && cached.includedSize === trendIncludedFlightIds.size
      && cached.sensitivity === detectionSensitivity
      && cached.reviewSignature === reviewSignature
    ) {
      return cached.key;
    }
    const flights = visibleFlightEntries()
      .filter((entry) => entry.aircraft === aircraft && flightIncludedInTrendAnalysis(entry))
      .map(trendFlightFingerprint)
      .sort()
      .join("||");
    const excludedFlights = visibleFlightEntries()
      .filter((entry) => entry.aircraft === aircraft && isSupportedFlight(entry) && !flightIncludedInTrendAnalysis(entry))
      .map((entry) => entry.id)
      .sort()
      .join(",");
    const profileHash = trendHashString(JSON.stringify(aircraftProfiles?.[aircraft] ?? null));
    const reviewSettingsHash = trendHashString(reviewSignature);
    const key = `${trendCacheSchemaVersion}|${detectionSensitivity}|${aircraft}|${profileHash}|${reviewSettingsHash}|${excludedFlights}|${flights}`;
    trendCacheKeyMemo.set(aircraft, {
      manifestRef: flightManifest,
      profilesRef: aircraftProfiles,
      excludedRef: trendExcludedFlightIds,
      includedRef: trendIncludedFlightIds,
      excludedSize: trendExcludedFlightIds.size,
      includedSize: trendIncludedFlightIds.size,
      sensitivity: detectionSensitivity,
      reviewSignature,
      key,
    });
    return key;
  }

  function clearTrendRowsForAircraft(aircraft) {
    if (!aircraft) return;
    trendCacheKeyMemo.delete(aircraft);
    const token = `|${aircraft}|`;
    [...trendCache.keys()].forEach((key) => {
      if (key.includes(token)) trendCache.delete(key);
    });
    [...trendEtagStore.keys()].forEach((key) => {
      if (key.includes(token)) trendEtagStore.delete(key);
    });
    clearStoredTrendRowsForAircraft(aircraft);
    // Phase 3i: drop the persisted (IndexedDB) snapshot too. Hygiene only — the server ETag would
    // self-heal a stale entry on the next conditional GET — so it is best-effort and async.
    const aircraftId = selectedTrendAircraftDatabaseId(aircraft);
    if (aircraftId && typeof deleteCachedTrendSnapshotsForAircraft === "function") {
      Promise.resolve(deleteCachedTrendSnapshotsForAircraft(aircraftId)).catch(() => {});
    }
  }

  function trendFeatureAccess(aircraft) {
    return aircraft ? getAircraftProfiles()?.[aircraft]?.featureAccess?.trends ?? null : null;
  }

  function syncTrendPlanGate(aircraft) {
    const access = trendFeatureAccess(aircraft);
    const gated = access?.allowed === false;
    const view = document.querySelector("#view-trends");
    const gate = document.querySelector("#trendPlanGate");
    view?.classList.toggle("is-plan-gated", gated);
    if (gate) gate.hidden = !gated;
    if (!gated) return false;
    const planLabel = String(access.planLabel ?? "This account").trim();
    const title = document.querySelector("#trendPlanGateTitle");
    const body = document.querySelector("#trendPlanGateBody");
    if (title) title.textContent = `Trends are not included with the ${planLabel} plan`;
    if (body) body.textContent = "Cross-flight comparisons and trend summaries are unavailable on this plan.";
    return true;
  }

  async function buildAircraftTrends(aircraft, options = {}) {
    if (trendFeatureAccess(aircraft)?.allowed === false) return [];
    const key = trendCacheKey(aircraft);
    if (trendCache.has(key)) return trendCache.get(key);
    const storedRows = readStoredTrendRows(key);
    if (storedRows) {
      trendCache.set(key, storedRows);
      return storedRows;
    }
    const flights = visibleFlightEntries()
      .filter((entry) => entry.aircraft === aircraft && flightIncludedInTrendAnalysis(entry));
    const rows = [];
    for (const [index, entry] of flights.entries()) {
      try {
        const parsedPoints = await parseFlightEntry(entry);
        const { points } = trimEndpointSamples(parsedPoints);
        if (!points.length) continue;
        // Off the main thread (Web Worker) with a persistent bundle-cache hit
        // when available; awaiting a synchronous fallback is a no-op, so the
        // loop stays correct when analyzeTrendFlight is not injected.
        const analyzeFlight = analyzeTrendFlight ?? calculateHealthSummary;
        const summary = await analyzeFlight(points, {
          selectedEntry: entry,
          aircraftProfile: aircraftProfileForEntry(entry),
        });
        if (options.isCanceled?.()) return null;
        rows.push(buildTrendRow(entry, points, summary, {
          engineDescriptors: summary.engineDescriptors,
        }));
      } catch (error) {
        rows.push({
          id: entry.id,
          title: flightDisplayTitle(entry),
          aircraft: entry.aircraft,
          monitor: entry.monitor,
          dateValue: trendDateValue(entry, []),
          dateLabel: trendDateLabel(trendDateValue(entry, [])),
          error: error.message,
        });
      }
      options.onProgress?.({ complete: index + 1, total: flights.length, entry });
      await waitForUiFrame();
      if (options.isCanceled?.()) return null;
    }
    if (options.isCanceled?.()) return null;
    const normalizedRows = normalizeTrendRowsForDisplay(rows, aircraft);
    trendCache.set(key, normalizedRows);
    writeStoredTrendRows(key, normalizedRows);
    return normalizedRows;
  }

  async function buildFreshTrendRows(aircraft, token) {
    return await buildAircraftTrends(aircraft, {
      isCanceled: () => token !== getTrendRequestToken(),
      onProgress: ({ complete, total }) => {
        if (token !== getTrendRequestToken() || !total) return;
        setTrendAnalysisBusy(true, { aircraft, complete, total });
      },
    });
  }

  // Phase 3i: the client-side trend-exclusion signature for an aircraft (sorted excluded supported
  // flight ids). The cached rows are already filtered by it, but the SERVER ETag is not — so it gates
  // the cache (a re-include must miss, not 304-rehydrate the pre-filtered rows). Mirrors the
  // excludedFlights component of trendCacheKey.
  function trendExclusionSignature(aircraft) {
    return visibleFlightEntries()
      .filter((entry) => entry.aircraft === aircraft && isSupportedFlight(entry) && !flightIncludedInTrendAnalysis(entry))
      .map((entry) => entry.id)
      .sort()
      .join(",");
  }

  // Phase 3i: read the persisted (IndexedDB) snapshot for the conditional GET. Returns { etag, rows }
  // or null. Best-effort; any failure (no IndexedDB, version/exclusion mismatch) yields null + a fetch.
  async function readPersistedTrendSnapshot(aircraft) {
    const aircraftId = selectedTrendAircraftDatabaseId(aircraft);
    if (!aircraftId || typeof readCachedTrendSnapshot !== "function") return null;
    try {
      return await readCachedTrendSnapshot(aircraftId, getDetectionSensitivity(), trendCacheSchemaVersion, trendExclusionSignature(aircraft));
    } catch {
      return null;
    }
  }

  // Phase 3i: persist a FRESH (200, non-stale) snapshot so a later reload can 304. Fire-and-forget.
  // The ETag covers the server body; the exclusion signature covers the client-side row filtering.
  function persistTrendSnapshot(aircraft, result) {
    const aircraftId = selectedTrendAircraftDatabaseId(aircraft);
    if (!aircraftId || typeof writeCachedTrendSnapshot !== "function") return;
    if (!result?.rows || !result.etag || result.stale) return;
    Promise.resolve(writeCachedTrendSnapshot(aircraftId, getDetectionSensitivity(), {
      etag: result.etag,
      rows: result.rows,
      snapshotVersion: result.snapshotVersion ?? trendCacheSchemaVersion,
      exclusionSignature: trendExclusionSignature(aircraft),
    })).catch(() => {});
  }

  async function maybeLoadDatabaseTrendRows(aircraft, key, { force = false } = {}) {
    // Phase 3i: send the persisted ETag so an unchanged aircraft answers 304 and we rehydrate from
    // the IndexedDB cache instead of re-downloading the multi-MB body. Skipped on an explicit force.
    // The persisted read is gated on the current exclusion signature, so a re-included flight misses.
    const cached = force ? null : await readPersistedTrendSnapshot(aircraft);
    const result = await readDatabaseTrendSnapshotRows(aircraft, { ifNoneMatch: cached?.etag ?? null });
    if (result.notModified && cached?.rows) {
      // 304: the cached snapshot is current. Rehydrate without a body download.
      trendCache.set(key, cached.rows);
      writeStoredTrendRows(key, cached.rows);
      trendEtagStore.set(key, cached.etag);
      return { rows: cached.rows, reason: "ok", stale: false, etag: cached.etag };
    }
    if (result.rows) {
      if (result.stale) {
        trendCache.delete(key);
        trendEtagStore.delete(key);
        removeStoredTrendRows(key);
      } else {
        trendCache.set(key, result.rows);
        writeStoredTrendRows(key, result.rows);
      }
      // Remember the ETag (fresh responses only) so the background revalidation can 304.
      if (result.etag && !result.stale) {
        trendEtagStore.set(key, result.etag);
        persistTrendSnapshot(aircraft, result);
      }
      return result;
    }
    return result;
  }

  // Headless snapshot warm-up: populate every cache tier (in-memory trendCache,
  // localStorage rows, IndexedDB snapshot + ETag) for an aircraft WITHOUT
  // rendering anything, so the first Trends-tab visit paints from cache instead
  // of paying the fetch+parse. No DOM writes, no analysis-busy state, and no
  // trendRequestToken mutation — a real load started mid-prefetch proceeds
  // untouched and simply finds the tiers warm (or 304s cheaply). Best-effort:
  // every failure is swallowed.
  const trendSnapshotPrefetchInflight = new Map();

  function prefetchTrendSnapshot(aircraft) {
    if (!aircraft) return Promise.resolve(null);
    if (trendFeatureAccess(aircraft)?.allowed === false) return Promise.resolve(null);
    const key = trendCacheKey(aircraft);
    if (cachedTrendRowsForDisplay(key)) return Promise.resolve(null);
    const inflight = trendSnapshotPrefetchInflight.get(key);
    if (inflight) return inflight;
    const request = maybeLoadDatabaseTrendRows(aircraft, key)
      .catch(() => null)
      .finally(() => trendSnapshotPrefetchInflight.delete(key));
    trendSnapshotPrefetchInflight.set(key, request);
    return request;
  }

  function scheduleTrendSnapshotRefresh(aircraft, originatingToken, delayMs = TREND_STALE_REFRESH_DELAY_MS, attempt = 0) {
    window.setTimeout(async () => {
      if (originatingToken !== getTrendRequestToken()) return;
      if (selectedTrendAircraft() !== aircraft) return;
      try {
        const key = trendCacheKey(aircraft);
        // Phase 3i: prefer the in-memory ETag, but fall back to the persisted (IndexedDB) one so a
        // RELOAD — which starts with an empty in-memory store — still revalidates with a conditional
        // GET and answers 304 instead of re-downloading the multi-MB body.
        let ifNoneMatch = trendEtagStore.get(key);
        if (!ifNoneMatch) {
          const persisted = await readPersistedTrendSnapshot(aircraft);
          ifNoneMatch = persisted?.etag ?? null;
        }
        const refreshed = await readDatabaseTrendSnapshotRows(aircraft, { ifNoneMatch });
        if (originatingToken !== getTrendRequestToken()) return;
        // 304: inputs unchanged since our cached ETag — the rows we hold are still current.
        if (refreshed.notModified) {
          if (ifNoneMatch) trendEtagStore.set(key, ifNoneMatch); // re-seat so later revalidations stay cheap
          return;
        }
        if (!refreshed.rows || refreshed.stale) {
          if (refreshed.stale && refreshed.reason !== "stale_analysis_runs") {
            if (attempt < TREND_STALE_REFRESH_MAX_ATTEMPTS) {
              scheduleTrendSnapshotRefresh(aircraft, originatingToken, TREND_STALE_REFRESH_DELAY_MS, attempt + 1);
            } else {
              syncTrendStaleNotice(aircraft, { stale: true, reason: "refresh_delayed" });
            }
          }
          return;
        }
        if (refreshed.etag) {
          trendEtagStore.set(key, refreshed.etag);
          persistTrendSnapshot(aircraft, refreshed);
        }
        const existing = trendCache.get(key);
        const existingSignature = existing ? JSON.stringify(existing) : "";
        const refreshedSignature = JSON.stringify(refreshed.rows);
        if (existingSignature === refreshedSignature) {
          syncTrendStaleNotice(aircraft, { stale: false });
          return;
        }
        trendCache.set(key, refreshed.rows);
        writeStoredTrendRows(key, refreshed.rows);
        renderTrendView(refreshed.rows, aircraft, { force: true, stale: false });
      } catch (_) {
        // Background refresh; ignore failures and let the user re-trigger if needed.
      }
    }, delayMs);
  }

  function renderCachedTrendView({ force = false } = {}) {
    const aircraft = selectedTrendAircraft();
    const key = trendCacheKey(aircraft);
    const rows = cachedTrendRowsForDisplay(key);
    if (rows) renderTrendView(rows, aircraft, { force });
  }

  function setTrendAnalysisStep(activeStep) {
    const order = ["gather", "review", "build"];
    const activeIndex = order.indexOf(activeStep);
    document.querySelectorAll("[data-trend-load-step]").forEach((item) => {
      const i = order.indexOf(item.dataset.trendLoadStep);
      item.classList.toggle("is-active", i === activeIndex);
      item.classList.toggle("is-complete", i >= 0 && i < activeIndex);
    });
  }

  function setTrendAnalysisBusy(active, { aircraft = "", complete = 0, total = 0, mode = "analysis" } = {}) {
    const view = document.querySelector("#view-trends");
    const overlay = document.querySelector("#trendAnalysisOverlay");
    const title = document.querySelector("#trendAnalysisTitle");
    const body = document.querySelector("#trendAnalysisBody");
    const cancel = document.querySelector("#trendAnalysisCancel");
    const status = document.querySelector("#trendStatus");
    if (view) view.classList.toggle("is-analyzing", active);
    if (overlay) overlay.hidden = !active;
    if (!active) return;
    const aircraftLabel = aircraft || "aircraft";
    if (mode === "loading") {
      const message = `Loading saved ${aircraftLabel} trend analysis...`;
      if (status) status.textContent = message;
      if (cancel) cancel.hidden = true;
      if (title) title.textContent = `Loading ${aircraftLabel} history`;
      if (body) body.textContent = "Loading the saved trend snapshot and preparing the cross-flight evidence view.";
      setTrendAnalysisStep("gather");
      return;
    }
    if (cancel) cancel.hidden = false;
    setTrendAnalysisStep("review");
    const progress = total ? ` ${complete}/${total}` : "";
    const message = `Analyzing ${aircraftLabel} logs${progress}...`;
    if (status) status.textContent = message;
    if (title) title.textContent = total ? `Analyzing ${aircraftLabel} logs ${complete}/${total}` : `Analyzing ${aircraftLabel} logs`;
    if (body) {
      const alary = document.createElement("span");
      alary.className = "brand-name-main";
      alary.textContent = "Alary";
      const iq = document.createElement("span");
      iq.className = "brand-name-accent";
      iq.textContent = "IQ";
      body.replaceChildren(
        document.createTextNode("Trend controls are paused while "),
        alary,
        iq,
        document.createTextNode(" reviews the log history for repeatable engine changes."),
      );
    }
  }

  // One designed presentation for every Trends empty/error state (Phase 5g): icon + title +
  // plain-language body + optional action button. The buttons carry data-trend-empty-action and
  // are handled by a single delegated listener in setupTrendControls.
  function trendEmptyStateHtml({ icon = "◌", title, body, action = null }) {
    return `
      <div class="trend-empty-state">
        <span class="trend-empty-state-icon" aria-hidden="true">${escapeHtml(icon)}</span>
        <strong>${escapeHtml(title)}</strong>
        <p>${escapeHtml(body)}</p>
        ${action ? `<button class="secondary-action" type="button" data-trend-empty-action="${escapeHtml(action.key)}">${escapeHtml(action.label)}</button>` : ""}
      </div>
    `;
  }

  function renderTrendCanceledView() {
    const historyContext = document.querySelector("#trendHistoryContext");
    if (historyContext) historyContext.innerHTML = "";
    setRenderedTrendSignature(`canceled:${Date.now()}`);
    setRenderedTrendDataSignature(getRenderedTrendSignature());
    setRenderedTrendSampleSignature("");
    setCurrentTrendRows(null);
    setCurrentTrendAircraft("");
    setCurrentTrendSensitivity("");
    syncTrendStaleNotice("", { stale: false });
    incrementTrendSampleGraphToken();
    const setText = (selector, value) => {
      const element = document.querySelector(selector);
      if (element) element.textContent = value;
    };
    setText("#trendVerdictCoverage", "Analysis canceled");
    setText("#trendVerdictWindow", "No trend data shown");
    setText("#trendVerdictTitle", "Trend analysis was canceled");
    setText("#trendVerdictBody", "No trend findings are shown because this aircraft history was not fully analyzed.");
    setText("#trendVerdictEvidence", "No evidence shown");
    setText("#trendVerdictAction", "Run trend analysis again when you are ready to review this aircraft history.");
    setText("#trendEvidenceCount", "No evidence rows shown");
    setText("#trendEvidenceFocus", "Table focus: none");
    setText("#trendChartTitle", "Sample flights");
    setText("#trendSampleIntro", "Sample traces are hidden until trend analysis completes.");
    setText("#trendSampleCount", "No sample flights");
    setText("#trendSampleWindowCount", "No graph windows");
    const verdictRibbon = document.querySelector("#trendVerdictRibbon");
    const verdictBand = document.querySelector("#trendVerdictBand");
    const verdictStatus = document.querySelector("#trendVerdictStatus");
    const metricTiles = document.querySelector("#trendMetricTiles");
    const findingsSummary = document.querySelector("#trendFindingsSummary");
    const samples = document.querySelector("#trendSampleGraphs");
    const tableBody = document.querySelector("#trendTableBody");
    if (verdictRibbon) verdictRibbon.className = "trend-verdict-ribbon status-muted";
    if (verdictBand) verdictBand.className = "trend-verdict-band trend-findings-band";
    if (verdictStatus) {
      verdictStatus.textContent = "Canceled";
      verdictStatus.className = "engine-status muted";
    }
    if (metricTiles) {
      metricTiles.innerHTML = trendEmptyStateHtml({
        icon: "◌",
        title: "Analysis canceled",
        body: "Run trend analysis again to populate these review areas.",
        action: { key: "retry", label: "Run analysis again" },
      });
    }
    if (findingsSummary) findingsSummary.innerHTML = `<p>No findings summary is shown because trend analysis was canceled.</p>`;
    if (samples) {
      samples.innerHTML = trendEmptyStateHtml({
        icon: "◌",
        title: "No sample flights",
        body: "Sample traces are hidden until trend analysis completes.",
      });
    }
    if (tableBody) tableBody.innerHTML = `<tr><td colspan="15">${trendEmptyStateHtml({ icon: "◌", title: "No evidence rows", body: "Trend analysis was canceled, so no flights are shown." })}</td></tr>`;
    const flightCards = document.querySelector("#trendFlightCards");
    if (flightCards) {
      flightCards.innerHTML = trendEmptyStateHtml({
        icon: "◌",
        title: "No flights shown",
        body: "Trend analysis was canceled before the flight log could be built.",
        action: { key: "retry", label: "Run analysis again" },
      });
    }
    setRenderedTrendTableSignature("");
  }

  function renderTrendUnavailableView({ aircraft, reason, error } = {}) {
    const historyContext = document.querySelector("#trendHistoryContext");
    if (historyContext) historyContext.innerHTML = "";
    setRenderedTrendSignature(`unavailable:${reason ?? "unknown"}:${Date.now()}`);
    setRenderedTrendDataSignature(getRenderedTrendSignature());
    setRenderedTrendSampleSignature("");
    setCurrentTrendRows(null);
    setCurrentTrendAircraft("");
    setCurrentTrendSensitivity("");
    syncTrendStaleNotice(aircraft ?? "", { stale: false });
    incrementTrendSampleGraphToken();
    const aircraftLabel = aircraft || "this aircraft";
    const noAnalysis = reason === "no_analysis_runs";
    // Flights were analyzed, but under an older analysis version, so none is current.
    // This is NOT "never analyzed" — guide the owner to re-analyze, not to upload.
    const staleAnalysis = reason === "stale_analysis_runs";
    const missingRoute = reason === "not_found";
    const title = staleAnalysis
      ? "Re-analysis needed"
      : noAnalysis ? "No analyzed flights yet" : missingRoute ? "Trend link unavailable" : "Trend analysis unavailable";
    const body = staleAnalysis
      ? `${aircraftLabel}'s flights were analyzed under an older analysis version, so its trend history is out of date. Re-analyze them to rebuild it.`
      : noAnalysis
        ? `No analysis runs are saved for ${aircraftLabel}. Upload flight logs and run analysis to build a trend snapshot.`
        : missingRoute
          ? "The linked aircraft trend could not be found or is not available to this account."
        : `Could not load the saved trend snapshot for ${aircraftLabel}. ${error ? error.message : "Try again, or re-run analysis from the Aircraft workspace."}`;
    const sampleNote = missingRoute
      ? "Sample traces are hidden because this aircraft trend is not available."
      : (noAnalysis || staleAnalysis)
      ? "Sample traces appear after analysis runs are saved."
      : "Sample traces are hidden until the trend snapshot is available.";
    const setText = (selector, value) => {
      const element = document.querySelector(selector);
      if (element) element.textContent = value;
    };
    setText("#trendVerdictCoverage", missingRoute ? "Link unavailable" : staleAnalysis ? "Re-analysis needed" : noAnalysis ? "No analysis runs" : "Snapshot unavailable");
    setText("#trendVerdictWindow", "No trend data shown");
    setText("#trendVerdictTitle", title);
    setText("#trendVerdictBody", body);
    setText("#trendVerdictEvidence", "No evidence available");
    setText("#trendVerdictAction", missingRoute
      ? "Check that the copied trend link belongs to an aircraft in this account."
      : staleAnalysis
      ? "Re-analyze this aircraft's flights to rebuild its trend history."
      : noAnalysis ? "Upload flight logs and run analysis to build this aircraft history." : "Re-run analysis or try loading the saved trend snapshot again.");
    setText("#trendEvidenceCount", "No evidence rows available");
    setText("#trendEvidenceFocus", "Table focus: none");
    setText("#trendChartTitle", "Sample flights");
    setText("#trendSampleIntro", sampleNote);
    setText("#trendSampleCount", "No sample flights");
    setText("#trendSampleWindowCount", "No graph windows");
    const verdictRibbon = document.querySelector("#trendVerdictRibbon");
    const verdictBand = document.querySelector("#trendVerdictBand");
    const verdictStatus = document.querySelector("#trendVerdictStatus");
    const metricTiles = document.querySelector("#trendMetricTiles");
    const findingsSummary = document.querySelector("#trendFindingsSummary");
    const samples = document.querySelector("#trendSampleGraphs");
    const tableBody = document.querySelector("#trendTableBody");
    if (verdictRibbon) verdictRibbon.className = "trend-verdict-ribbon status-muted";
    if (verdictBand) verdictBand.className = "trend-verdict-band trend-findings-band";
    if (verdictStatus) {
      verdictStatus.textContent = noAnalysis ? "No data" : "Unavailable";
      verdictStatus.className = "engine-status muted";
    }
    const stateIcon = staleAnalysis ? "⟳" : noAnalysis ? "↑" : "⚠";
    const stateAction = missingRoute
      ? null
      : staleAnalysis
      ? { key: "reanalyze", label: "Re-analyze flights" }
      : noAnalysis
        ? { key: "upload", label: "Go to uploads" }
        : { key: "retry", label: "Try again" };
    if (metricTiles) metricTiles.innerHTML = trendEmptyStateHtml({ icon: stateIcon, title, body, action: stateAction });
    if (findingsSummary) findingsSummary.innerHTML = `<p>${escapeHtml(body)}</p>`;
    if (samples) samples.innerHTML = trendEmptyStateHtml({ icon: stateIcon, title: "No sample flights", body: sampleNote });
    if (tableBody) tableBody.innerHTML = `<tr><td colspan="15">${trendEmptyStateHtml({ icon: stateIcon, title, body })}</td></tr>`;
    const flightCards = document.querySelector("#trendFlightCards");
    if (flightCards) flightCards.innerHTML = trendEmptyStateHtml({ icon: stateIcon, title, body, action: stateAction });
    setRenderedTrendTableSignature("");
  }

  function cancelTrendAnalysis() {
    const aircraft = selectedTrendAircraft();
    incrementTrendRequestToken();
    clearTrendRowsForAircraft(aircraft);
    setTrendAnalysisBusy(false);
    renderTrendCanceledView();
    const status = document.querySelector("#trendStatus");
    if (status) status.textContent = "Trend analysis canceled";
  }

  async function renderTrendsForSelection({ force = false } = {}) {
    const aircraft = selectedTrendAircraft();
    syncTrendRoute(aircraft);
    const status = document.querySelector("#trendStatus");
    if (!aircraft) {
      syncTrendPlanGate(null);
      setTrendAnalysisBusy(false);
      renderTrendView([], "");
      return;
    }
    if (syncTrendPlanGate(aircraft)) {
      incrementTrendRequestToken();
      setTrendAnalysisBusy(false);
      if (status) status.textContent = "Trends unavailable on this plan";
      return;
    }
    const key = trendCacheKey(aircraft);
    const token = incrementTrendRequestToken();
    if (force) {
      trendCache.delete(key);
      trendEtagStore.delete(key);
      removeStoredTrendRows(key);
    } else {
      const cachedRows = cachedTrendRowsForDisplay(key);
      if (cachedRows) {
        // Cache-first: paint the saved rows immediately with no loading overlay.
        // In DB mode, revalidate against the server snapshot in the background so
        // newer data is picked up without blocking the view on every visit.
        setTrendAnalysisBusy(false);
        renderTrendView(cachedRows, aircraft, { force });
        if (status) status.textContent = "";
        scheduleTrendSnapshotRefresh(aircraft, token, 0);
        return;
      }
    }
    {
      setTrendAnalysisBusy(true, { aircraft, mode: "loading" });
      let result;
      try {
        result = await maybeLoadDatabaseTrendRows(aircraft, key, { force });
      } catch (error) {
        if (token !== getTrendRequestToken()) return;
        console.warn("Could not load saved trend snapshot.", error);
        setTrendAnalysisBusy(false);
        renderTrendUnavailableView({ aircraft, reason: "fetch_error", error });
        if (status) status.textContent = `Unable to load ${aircraft} trend snapshot`;
        return;
      }
      if (token !== getTrendRequestToken()) return;
      if (result.rows) {
        const ready = renderTrendView(result.rows, aircraft, { force, stale: result.stale, staleReason: result.reason });
        setTrendAnalysisStep("build");
        await awaitTrendRenderSettled(ready);
        if (token !== getTrendRequestToken()) return;
        setTrendAnalysisBusy(false);
        if (status) status.textContent = "";
        if (result.stale && result.reason !== "stale_analysis_runs") scheduleTrendSnapshotRefresh(aircraft, token);
        return;
      }
      if (result.stale) {
        setTrendAnalysisBusy(true, { aircraft, mode: "loading" });
        if (status) status.textContent = `Refreshing ${aircraft} trend snapshot`;
        window.setTimeout(() => {
          if (token !== getTrendRequestToken()) return;
          renderTrendsForSelection({ force: true });
        }, 5000);
        return;
      }
      if (result.reason === "version_mismatch") {
        console.warn(
          `Trend snapshot version mismatch for ${aircraft}: expected ${result.expectedVersion}, received ${result.snapshotVersion}. Rebuilding from flight history.`,
        );
        if (status) status.textContent = `Rebuilding ${aircraft} trend snapshot`;
        setTrendAnalysisBusy(true, { aircraft });
        const rows = await buildFreshTrendRows(aircraft, token);
        if (token !== getTrendRequestToken()) return;
        if (!rows) {
          setTrendAnalysisBusy(false);
          renderTrendCanceledView();
          if (status) status.textContent = "Trend analysis canceled";
          return;
        }
        try {
          const savedRows = await saveDatabaseTrendSnapshotRows?.(aircraft, rows);
          if (savedRows?.length) {
            trendCache.set(key, savedRows);
            writeStoredTrendRows(key, savedRows);
          }
        } catch (error) {
          console.warn("Could not save rebuilt trend snapshot.", error);
        }
        const ready = renderTrendView(rows, aircraft, { force: true });
        setTrendAnalysisStep("build");
        await awaitTrendRenderSettled(ready);
        if (token !== getTrendRequestToken()) return;
        setTrendAnalysisBusy(false);
        if (status) status.textContent = "";
        return;
      }
      setTrendAnalysisBusy(false);
      renderTrendUnavailableView({ aircraft, reason: result.reason });
      if (status) {
        status.textContent = result.reason === "no_analysis_runs"
          ? `No analyzed flights yet for ${aircraft}`
          : result.reason === "stale_analysis_runs"
            ? `Re-analysis needed for ${aircraft}`
            : `Trend snapshot unavailable for ${aircraft}`;
      }
      return;
    }
    setTrendAnalysisBusy(true, { aircraft });
    try {
      const rows = await buildFreshTrendRows(aircraft, token);
      if (token !== getTrendRequestToken()) return;
      if (!rows) {
        setTrendAnalysisBusy(false);
        renderTrendCanceledView();
        if (status) status.textContent = "Trend analysis canceled";
        return;
      }
      const ready = renderTrendView(rows, aircraft, { force });
      setTrendAnalysisStep("build");
      await awaitTrendRenderSettled(ready);
      if (token !== getTrendRequestToken()) return;
      setTrendAnalysisBusy(false);
    } catch (error) {
      if (token !== getTrendRequestToken()) return;
      setTrendAnalysisBusy(false);
      if (status) status.textContent = `Unable to analyze ${aircraft} logs`;
      throw error;
    }
  }

  function renderTrendView(rows, aircraft, options = {}) {
    setCurrentTrendRows(rows);
    setCurrentTrendAircraft(aircraft);
    setCurrentTrendSensitivity(getDetectionSensitivity());
    let historyStart = "";
    try { historyStart = localStorage.getItem(`alaryiq.trendHistoryStart:${aircraft}`) || ""; } catch {}
    const startTime = /^\d{4}-\d{2}-\d{2}$/.test(historyStart) ? Date.parse(`${historyStart}T00:00:00Z`) : NaN;
    const preferredRows = trendRowsIncludedByPreference(rows);
    const includedRows = preferredRows.filter((row) => !Number.isFinite(startTime) || (Number.isFinite(row.dateValue) && row.dateValue >= startTime));
    const scope = document.querySelector("#trendHistoryContext");
    if (scope) {
      const ordered = includedRows.filter((row) => !row.error).slice().sort((a, b) => a.dateValue - b.dateValue);
      const contextCount = ordered.filter((row) => row.operatingContext).length;
      const excluded = rows.length - includedRows.length;
      const unavailable = includedRows.filter((row) => row.error).length;
      const reasonCounts = trendExclusionCounts(aircraft);
      const reasonText = Object.entries(reasonCounts).map(([label, count]) => `${count} ${label}`).join("; ");
      scope.innerHTML = `<p>${escapeHtml(aircraft)} · ${escapeHtml(getDetectionSensitivity())} sensitivity · ${ordered.length} usable flights · ${escapeHtml(ordered[0]?.dateLabel || "No start date")} to ${escapeHtml(ordered.at(-1)?.dateLabel || "no latest date")}</p>
        <label>History starts <input id="trendHistoryStart" type="date" value="${escapeHtml(historyStart)}" aria-describedby="trendHistoryHelp"></label>
        <small id="trendHistoryHelp">Choose a date after maintenance or a sensor change to start a new comparison history. Clear it to restore the full window.</small>
        <details><summary>Coverage and exclusions</summary><p>${excluded} loaded rows outside this selection. ${preferredRows.length - includedRows.length} removed by the history start date. ${unavailable} selected flights have unavailable analysis. ${contextCount} of ${ordered.length} usable flights contain operating context for comparison.</p><p>${escapeHtml(reasonText)}</p><p>History uses the most recent 50 eligible flights or 100 hours. Flights shorter than 15 minutes are excluded. Missing metric scores stay unavailable.</p></details>`;
    }
    const signature = trendRenderSignature(includedRows, aircraft);
    const dataSignature = trendDataRenderSignature(includedRows, aircraft);
    // Distinguish a real data change from a selection-only re-render (a metric
    // tile click forces a re-render with options.force but identical data). Only
    // a true data change needs the aircraft workspace shell and the visual
    // workspace graph cards rebuilt; selection changes just restyle them.
    const trendDataChanged = dataSignature !== getRenderedTrendDataSignature();
    if (!options.force && signature === getRenderedTrendSignature() && getRenderedTrendFlushed()) {
      if (Object.hasOwn(options, "stale")) {
        syncTrendStaleNotice(aircraft, { stale: options.stale, reason: options.staleReason });
      }
      return;
    }
    if (trendDataChanged && getSelectedAircraftPanel() === "trends") renderAircraftWorkspace();
    setRenderedTrendSignature(signature);
    setRenderedTrendDataSignature(dataSignature);
    setRenderedTrendFlushed(false);
    const usableRows = includedRows.filter((row) => !row.error);
    if (Object.hasOwn(options, "stale")) {
      syncTrendStaleNotice(aircraft, { stale: options.stale, reason: options.staleReason });
    } else {
      syncTrendStaleNotice(aircraft, staleTrendSnapshotState?.aircraft === aircraft
        ? { stale: true, reason: staleTrendSnapshotState.reason }
        : { stale: false });
    }
    updateTrendMetricControls(usableRows);
    renderTrendOwnerStory(includedRows, usableRows, aircraft);
    renderTrendVisualWorkspace?.(includedRows, aircraft, { dataChanged: trendDataChanged });
    renderTrendSummary(usableRows);
    renderTrendTable(includedRows);
    const sampleGraphsReady = renderTrendSampleGraphs(usableRows);
    scheduleTrendSampleGraphPrewarm(usableRows, { delayMs: 4200 });
    setRenderedTrendFlushed(true);
    return sampleGraphsReady;
  }

  async function awaitTrendRenderSettled(ready) {
    // Hold the loader until the visible sample graphs finish drawing (each parses
    // its sample flight), capped so an unusually slow payload can't pin the
    // overlay open. The off-screen prewarm stays deferred/idle-gated as before.
    await Promise.race([
      Promise.resolve(ready),
      new Promise((resolve) => window.setTimeout(resolve, 8000)),
    ]);
    await waitForUiFrame(60);
  }

  function trendEvidenceReturnContext(entry) {
    return {
      aircraft: entry?.aircraft ?? selectedTrendAircraft(),
      flightId: entry?.id ?? "",
      trendMetric: getActiveTrendMetric(),
    };
  }

  function updateTrendReturnControls() {
    const button = document.querySelector("#chartTrendReturn");
    if (!button) return;
    const dashboardActive = document.querySelector("#aircraftDashboardPanel")?.classList.contains("is-active") ?? false;
    const visible = dashboardActive && getTrendReturnContext()?.flightId === getSelectedFlight()?.id;
    button.hidden = !visible;
    if (!button.dataset.boundTrendReturn) {
      button.addEventListener("click", () => returnToTrendEvidence());
      button.dataset.boundTrendReturn = "true";
    }
  }

  function trendEvidenceTopReviewItem(summary = getHealthSummary()) {
    if (!summary) return null;
    const reviewItems = engineReviewItems(summary);
    return reviewItems.find((item) => item.windows?.length) ?? reviewItems[0] ?? null;
  }

  function focusTrendEvidenceFlightReview(summary = getHealthSummary()) {
    const item = trendEvidenceTopReviewItem(summary);
    setSelectedCylinderNumber(null);
    setSelectedEngineScope("aircraft");
    setSelectedHealthKey(item?.key ?? null);
    const selectedHealthKey = getSelectedHealthKey();
    if (selectedHealthKey) selectCompanionSeriesForHealthCard(selectedHealthKey);
    if (summary) renderHealthSummary(summary);
    const detectionWindow = selectedDetectionWindows(summary)[0] ?? item?.windows?.[0] ?? null;
    if (Number.isFinite(detectionWindow?.startTime)) {
      const endTime = Number.isFinite(detectionWindow.endTime) ? detectionWindow.endTime : detectionWindow.startTime;
      setPlaybackScrubElapsed((detectionWindow.startTime + endTime) / 2, { draw: false, resetZoom: true });
    }
    updateHealthCardSelection();
    scheduleDrawAll();
    return item;
  }

  function markReturnedTrendEvidenceRow(flightId) {
    if (!flightId) return;
    requestAnimationFrame(() => {
      const row = document.querySelector(`#trendTableBody [data-flight-id="${CSS.escape(flightId)}"], #trendFlightCards [data-flight-id="${CSS.escape(flightId)}"]`);
      if (!row) return;
      row.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
      row.classList.add("is-return-target");
      window.setTimeout(() => row.classList.remove("is-return-target"), 1800);
    });
  }

  async function returnToTrendEvidence() {
    const context = getTrendReturnContext();
    setTrendReturnContext(null);
    updateTrendReturnControls();
    if (context?.trendMetric && trendMetricMeta[context.trendMetric]) setActiveTrendMetric(context.trendMetric);
    await setAircraftPanel("trends");
    syncTrendRoute(context?.aircraft ?? selectedTrendAircraft());
    renderCurrentTrendView({ force: true });
    markReturnedTrendEvidenceRow(context?.flightId);
  }

  async function openTrendFlight(id, options = {}) {
    const flightManifest = getFlightManifest();
    const entry = flightManifest.find((flight) => flight.id === id)
      // DB-snapshot rows carry the Postgres flight UUID; older manifest entries may expose it only
      // under database.flightId, so fall back to that key before giving up.
      ?? flightManifest.find((flight) => flight?.database?.flightId === id);
    if (!entry || !isSupportedFlight(entry)) {
      console.warn("[trends] openTrendFlight: cannot open evidence flight", {
        id,
        found: Boolean(entry),
        parser: entry?.parser ?? null,
        manifestSize: flightManifest.length,
      });
      const evidenceCount = document.querySelector("#trendEvidenceCount");
      if (evidenceCount) {
        const previous = evidenceCount.textContent;
        evidenceCount.textContent = entry
          ? "This flight's log format can't be opened in the viewer"
          : "This flight isn't loaded in the app yet — try reloading the page";
        window.setTimeout(() => {
          if (evidenceCount.textContent.startsWith("This flight")) evidenceCount.textContent = previous;
        }, 4000);
      }
      return;
    }
    const fromTrendEvidence = options.source === "trendEvidence";
    if (fromTrendEvidence) setTrendReturnContext(trendEvidenceReturnContext(entry));
    try {
      await loadFlightLog(entry, { preserveTrendReturn: fromTrendEvidence, activateTrial: true });
    } catch (error) {
      if (fromTrendEvidence) setTrendReturnContext(null);
      throw error;
    }
    if (fromTrendEvidence) focusTrendEvidenceFlightReview(getHealthSummary());
    activateView("dashboard");
    if (fromTrendEvidence) {
      // Re-anchor on the current path: a bare "#dashboard" would resolve
      // against <base href="/"> and drop the tail-number path segment.
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#dashboard`);
      selectReportPanel("overview");
      scrollFlightChartPanelIntoView();
    }
  }

  return {
    trendAircraftOptions,
    setupTrendControls,
    renderCurrentTrendView,
    populateTrendAircraftSelect,
    selectedTrendAircraft,
    selectedTrendAircraftDatabaseId,
    trendCacheKey,
    clearTrendRowsForAircraft,
    buildAircraftTrends,
    maybeLoadDatabaseTrendRows,
    prefetchTrendSnapshot,
    scheduleTrendSnapshotRefresh,
    renderCachedTrendView,
    setTrendAnalysisBusy,
    renderTrendCanceledView,
    renderTrendUnavailableView,
    cancelTrendAnalysis,
    renderTrendsForSelection,
    renderTrendView,
    trendEvidenceReturnContext,
    updateTrendReturnControls,
    trendEvidenceTopReviewItem,
    focusTrendEvidenceFlightReview,
    markReturnedTrendEvidenceRow,
    returnToTrendEvidence,
    openTrendFlight,
  };
}
